/**
 * Bending Spoons SDK (Orion experiments + PicoX analytics) wiring for
 * listings-webapp. Mirrors the Valkyrie-app pattern documented in
 * `docs/orion-feature-flags.md` (creator-navigation PR #381):
 *
 *   - The provider is mounted once at the highest stable spot in the React
 *     tree (`pages/_app.page.tsx`).
 *   - This module owns the typed `Settings` shape for the whole app.
 *     One key per Orion flag, default = current production behavior.
 *   - Feature code never imports `useOrionSettings` directly; it imports a
 *     typed wrapper hook from this file (e.g. `useIsRelatedEventsDenseAdsEnabled`).
 *
 * To add a new flag: append one key + one default to `ListingsWebappSettings`
 * / `DEFAULT_SETTINGS`, then export a one-line wrapper hook below.
 */
import { useMemo } from 'react';
import { createAppDefaults } from '@eventbrite/bsp-sdk';
import { useOrionSettings } from '@eventbrite/bsp-sdk/react';

// Paid-weight sweep arms for the linear ranker (search_service `linear_paid_mode`
// field). Each arm pins the `is_paid` slot at a fixed share of the final score
// (sweep_1..3 → 0/10/15%); control gets the base weight.
export const LINEAR_PAID_MODES = ['sweep_1', 'sweep_2', 'sweep_3'] as const;

export type LinearPaidMode = (typeof LINEAR_PAID_MODES)[number];

// Server-side validation for the client-supplied paid-sweep arm. The value arrives
// from an untrusted source (a query param, which may be a `string[]` for repeated
// params), so coerce it to a known arm or `undefined` — never forward it raw. Mirrors
// the client `.find` in `useLinearPaidMode` so the allowlist is authoritative on the
// server too, not merely advisory (the Orion gate stays the real authority).
export const coerceLinearPaidMode = (value: unknown): LinearPaidMode | undefined =>
  LINEAR_PAID_MODES.find((mode) => mode === value);

export type ListingsWebappSettings = {
  enable_increase_impressions_on_related_events: boolean;
  show_follow_venue_cta: boolean;
  show_follow_artist_cta: boolean;
  members_experiments_excluded_organizer_ids: string[];
  related_higher_price: boolean;
  linear_paid_mode: string;
};

const DEFAULT_SETTINGS = createAppDefaults<ListingsWebappSettings>({
  enable_increase_impressions_on_related_events: false,
  show_follow_venue_cta: false,
  show_follow_artist_cta: false,
  members_experiments_excluded_organizer_ids: [],
  related_higher_price: false,
  linear_paid_mode: '',
});

export const useBspConfig = () =>
  useMemo(() => {
    const domain = typeof window !== 'undefined' ? window.location.hostname : '';
    const environment = domain.includes('evbqa')
      ? 'staging'
      : domain.includes('localhost')
        ? 'development'
        : 'production';

    return {
      appName: 'listings-webapp',
      environment,
      defaultOrionSettings: DEFAULT_SETTINGS,
      defaultSegmentations: {},
    } as const;
  }, []);

/**
 * Gate for the denser related-events ad layout
 * (`enable_increase_impressions_on_related_events` Orion flag).
 *
 *   false (default / control) — request `slots: 2` and interleave ads via
 *     `interleaveArrays(promoted, organic)` (positions [1, 3]).
 *   true  (treatment)         — request `slots: 3` and interleave at
 *     positions [1, 2, 4] (ads → ads → organic → ads → organic).
 *
 * Same key + same default in eb-ui's `packages/attendee/listings/src/components/_shared`
 * and `packages/attendee/event-recommendations/src` so all three surfaces
 * ramp in lockstep.
 *
 * Excluded organizers: when `parentEventOrganizerId` is one of the ids in
 * the Orion setting `members_experiments_excluded_organizer_ids`, this
 * hook returns `false` regardless of the assigned segment — mirroring
 * the eb-ui checkout / event-recommendations gate so all three surfaces
 * opt the same events out in lockstep.
 */
export const useIsRelatedEventsDenseAdsEnabled = (parentEventOrganizerId?: string): boolean => {
  const { settings } = useOrionSettings<ListingsWebappSettings>();
  const isOrganizerExcluded =
    !!parentEventOrganizerId && settings.members_experiments_excluded_organizer_ids.includes(parentEventOrganizerId);
  if (isOrganizerExcluded) {
    return false;
  }
  return settings.enable_increase_impressions_on_related_events;
};

/** Gate for the Follow Venue CTA (`show_follow_venue_cta` Orion flag). */
export const useShowFollowVenueCta = (): boolean => {
  const { settings } = useOrionSettings<ListingsWebappSettings>();
  return settings.show_follow_venue_cta;
};

/** Gate for the Follow Artist CTA (`show_follow_artist_cta` Orion flag). */
export const useShowFollowArtistCta = (): boolean => {
  const { settings } = useOrionSettings<ListingsWebappSettings>();
  return settings.show_follow_artist_cta;
};

/**
 * Gate for the higher-price related events experiment (`related_higher_price`
 * Orion flag). When on, the related-events request asks the search service to
 * zero out related events cheaper than the current event, so the carousel skews
 * towards equal-or-higher-priced events.
 *
 * Returns `isLoading` (unlike the other gates) so the related-events fetch can
 * wait for Orion to settle — otherwise a treatment visitor whose Orion resolves
 * after the fetch fires would be read as control and silently get the
 * unfiltered carousel, contaminating the experiment.
 */
export const useIsRelatedHigherPriceEnabled = (): { isEnabled: boolean; isLoading: boolean } => {
  let settings: Partial<ListingsWebappSettings> = {};
  let isLoading = false;
  try {
    // Swallow — useOrionSettings throws on the SSR render path (no BspProvider).
    const state = useOrionSettings<ListingsWebappSettings>();
    settings = state.settings;
    isLoading = state.isLoading;
  } catch {}
  return { isEnabled: settings.related_higher_price ?? false, isLoading };
};

/**
 * Gate for the linear paid-weight sweep experiment (`linear_paid_mode` Orion
 * flag). This replaces the `enable_linear_score` gate once the linear ranker is
 * the search-service default: enrolled visitors get a sweep arm, and the
 * related-events request forwards it as `linear_paid_mode` (alongside
 * `score_mode: 'linear'`) so the search service applies that arm's paid weight
 * under the linear ranker.
 *
 * Same `linear_paid_mode` key as the eb-ui discover surfaces (`useLinearPaidMode`)
 * and homepage-webapp, so every surface ramps in lockstep off a single switch.
 * Returns `undefined` for control / unrecognised values.
 *
 * Returns `isLoading` (like the higher-price gate) so the related-events fetch
 * can wait for Orion to settle before firing — otherwise a treatment visitor
 * whose Orion resolves after the fetch would be read as control and silently
 * get the base paid weight, contaminating the experiment.
 */
export const useLinearPaidMode = (): { paidMode: LinearPaidMode | undefined; isLoading: boolean } => {
  let settings: Partial<ListingsWebappSettings> = {};
  let isLoading = false;
  try {
    // Swallow — useOrionSettings throws on the SSR render path (no BspProvider).
    const state = useOrionSettings<ListingsWebappSettings>();
    settings = state.settings;
    isLoading = state.isLoading;
  } catch {}
  const paidMode = LINEAR_PAID_MODES.find((mode) => mode === settings.linear_paid_mode);
  return { paidMode, isLoading };
};
